import React, { useState, useEffect } from "react";
import { parseNumber } from "@brightspace-ui/intl/lib/number.js";
import { rpName } from "storybook-dashboard/dashboard/project/reportStatus";

import Octicon from "react-octicon";
import { Tooltip } from "react-tippy";
import Moment from "moment";
import TextareaAutosize from "react-textarea-autosize";
import { getConversionFactor, MetricAlerts, RequiredStar } from "../metrics/metricRow";

import RowErrorBoundary, { MiniErrorBoundary, CellSpanErrorBoundary } from "./error";
import { SubNodes } from "./node";

import SearchChildRow from "./search";
import SelectChildRow from "./selectChildren";
import SelectTwinChildRow from "./selectTwinChildren";
import { setAndShowModal, clearModal } from "AppSrc/utils/modal";

import { v4 as uuidv4 } from "uuid";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";

import ChartJSWrapper from "AppSrc/charts/chartjs";
import { getAverage } from "storybook-dashboard/utils/index";
import { roundDecimals } from "storybook-dashboard/dashboard/charts/utils";
import { Hider, isRequiredOnFreq } from "storybook-dashboard/utils";
import { isNumber } from "./dataTable/validateFields";
import Im from "immutable";

function DisabledInput({ value }) {
  return (
    <input
      type="number"
      disabled={true}
      className={`form-control-plaintext m-0 p-0`}
      name={"value"}
      value={value === null ? "" : value}
      style={{
        backgroundColor: "#e9ecef",
      }}
    />
  );
}

export function ReportNoInputCheckbox({
  hide,
  calculated,
  disableInputs,
  value,
  path,
  context: { setInputValue },
  score,
}) {
  if (hide) {
    return <td className="border-0" />;
  }
  let isDisabled = disableInputs || calculated;

  let id = path + "-noinput";

  const label = score.getIn(["metric", "name"]) + "Not Applicable";

  return (
    <td className="text-center border-0">
      <label htmlFor={id} hidden>
        Not Applicable
      </label>
      <input
        type="checkbox"
        disabled={isDisabled}
        className={`m-0 p-0`}
        defaultChecked={value?.getInPath("meta_json.noReport")}
        onChange={(e) => {
          setInputValue(
            Im.Map({
              _path: path,
              value: null,
              meta_json: { noReport: e.target.checked, disableValueInput: e.target.checked },
            })
          );
        }}
        name={"noreport"}
        id={id}
        aria-label={label}
      />
    </td>
  );
}

export function ReportValueInputCell(props) {
  let {
    context: { setInputValue, isCalculated, historicInputsByBaseMetric, largeDifferenceReported },
    disableInputs,
    disableValueInput,
    hide,
    isValid,
    path,
    score,
    value: valueObject,
  } = props;

  if (hide) return null;

  let value = valueObject?.get("value");
  const [tmpValue, setTmpValue] = useState(value);
  const [isInFocus, setIsInFocus] = useState(false);
  const baseMetricId = score.getIn(["metric", "uid"]);

  const calculated = isCalculated(score);

  const historicValues = historicInputsByBaseMetric?.get(baseMetricId, Im.List());
  const historicAverage = getAverage(historicValues?.map((v) => v.get("value")).toJS());
  const historicVarianceWarning = largeDifferenceReported({ baseMetricId, value });

  useEffect(() => {
    if (valueObject?.getIn(["meta_json", "noReport"]) && tmpValue !== valueObject.get("value")) {
      setTmpValue(valueObject.get("value"));
    }
  });

  let invalidFeedback = null;
  if (!isValid && isValid !== undefined) {
    invalidFeedback = <div className="invalid-feedback">Number required</div>;
  }

  let _value = isInFocus ? tmpValue : value;

  const valueVarianceWarning = (
    <Tooltip
      style={{ cursor: "pointer" }}
      position="bottom"
      html={
        <div>
          <u>Alert</u>: this value is significantly different to the average <u>{roundDecimals(historicAverage)}</u>
          {historicValues.size > 0 && ` of the previous ${historicValues.size} entries`}.
          <br /> Please check before submitting.
        </div>
      }
    >
      <Octicon name="alert" className="text-warning mt-1 mr-1" />
      <span className="badge badge-warning m-0">Check</span>
    </Tooltip>
  );

  const onBlur = (e) => {
    let newValue = parseNumber(e.target.value);
    if (e.target.value === "" || isNaN(newValue)) {
      newValue = null;
    }
    setInputValue(Im.Map({ _path: path, value: newValue }));
    setTmpValue(newValue);
  };

  if (calculated || disableInputs) {
    return (
      <td className="border-0">
        <DisabledInput value={_value} />
        {historicVarianceWarning ||
        (!historicValues?.isEmpty() &&
          isNumber(_value) &&
          (historicAverage > _value * 2 || historicAverage < _value / 2))
          ? valueVarianceWarning
          : null}
      </td>
    );
  }

  const handleChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/[^0-9.,]/g, "");
    setTmpValue(inputValue);
  };

  const handleFocus = () => {
    setIsInFocus(true);
    setTmpValue(value);
  };

  const label = score.getIn(["metric", "name"]) + " " + score.getIn(["metric", "unit"]);

  return (
    <td className="border-0">
      <label htmlFor={path} hidden>
        Value for: {score.getIn(["metric", "name"])} {score.getIn(["metric", "unit"])}
      </label>
      <input
        disabled={calculated || disableValueInput || score.noReport}
        style={{ height: "auto", display: "inline-block" }}
        className={`form-control m-0 p-0 ${invalidFeedback ? "is-invalid" : null} ${
          historicVarianceWarning ? "border-warning" : null
        }`}
        name={"value"}
        value={_value == null ? "" : _value}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={handleFocus}
        id={path}
        aria-label={label}
      />
      {invalidFeedback}
      {historicVarianceWarning ? valueVarianceWarning : null}
    </td>
  );
}

const dateToStr = (date, addDays) => {
  let _date = Moment(date);
  _date = _date && addDays ? _date.add(addDays, "days") : _date;
  return _date ? _date.format("Do MMM YY") : "";
};

export function ReportPeriodText({ isRequiredPeriodically, preamble }) {
  if (!isRequiredPeriodically) {
    return null;
  }
  let { from_date, dueDate } = isRequiredPeriodically;
  let _preamble = preamble || "For period from";
  return `${_preamble} ${dateToStr(from_date)} to ${dateToStr(dueDate, -1)}`;
}

function MetricPlaceholderText({ score, isRequiredPeriodically }) {
  if (!score) {
    return "";
  }
  let freqText = "";
  let placeholderText = score.getIn(["meta_json", "placeholder"]) || "";
  if (isRequiredPeriodically) {
    freqText = ReportPeriodText({ isRequiredPeriodically }) || "";
    freqText = placeholderText ? ` (${freqText})` : freqText;
  }
  return placeholderText + freqText;
}

export function ReportCommentCell(props) {
  let {
    hide,
    score,
    disableInputs,
    disableComments,
    disableValueInput,
    value: valueObject,
    path,
    colSpan,
    context: { isCalculated, setInputValue },
  } = props;
  let comment = valueObject?.get("comment") || "";
  let [tmpComment, setTmpComment] = useState(comment);

  if (hide || disableComments) return null;

  const onBlur = (e) => {
    if (tmpComment != comment) {
      setInputValue(Im.Map({ _path: path, comment: tmpComment }));
    }
  };

  const calculated = isCalculated(score);
  let disabled = calculated || disableInputs || disableValueInput;
  let CommentInputComponent = calculated ? "input" : TextareaAutosize;
  let placeholder = calculated ? "Calculated from sub-metrics" : MetricPlaceholderText(props) || "";

  let _id = `${path}-comment`;
  const label = "Comment for: " + score.getIn(["metric", "name"]);

  return (
    <td colSpan={colSpan || 1} className="border-0">
      <label htmlFor={_id} hidden>
        Comment for: {score.getIn(["metric", "name"])}
      </label>
      <CommentInputComponent
        type="text"
        className={`form-control-plaintext m-0 p-0`}
        name={"comment"}
        disabled={disabled}
        value={calculated ? "" : tmpComment}
        placeholder={placeholder}
        onChange={(e) => setTmpComment(e.target.value)}
        onBlur={onBlur}
        aria-label={label}
        id={_id}
      />
    </td>
  );
}

export function ToggleState({ id, labelText, checkedState, state, onChange, disableInputs }) {
  return (
    <React.Fragment>
      <input id={id} name={`${id}-state-d`} type="radio" checked={state === checkedState} readOnly={true} />
      <label
        htmlFor={id}
        onClick={(e) => {
          if (!disableInputs) onChange(checkedState);
        }}
        className={`text-nowrap flex-fill rounded-pill ${
          state === checkedState && disableInputs
            ? "bg-light text-secondary"
            : state === checkedState
            ? "bg-white text-dark border border-dark"
            : ""
        }`}
      >
        {labelText}
      </label>
    </React.Fragment>
  );
}

// This returns the state for a BooleanInput not a boolean from a state
const getBooleanState = (value) => {
  return [null, undefined].includes(value) ? null : value > 0;
};

const stateToValue = (state) => {
  return [null, undefined].includes(state) ? null : state == false ? 0 : 1;
};

function BooleanToggleCell(props) {
  const { colSpan, value, handleInputChange, disableInputs } = props;
  const [state, setState] = useState(getBooleanState(value?.get("value")));

  // Generate a uuid for the on/na/off ids
  let uid = uuidv4();
  let _props = {
    state,
    onChange: (e) => {
      setState(e);
      handleInputChange({
        target: {
          value: stateToValue(e),
        },
      });
    },
    ...props,
  };

  return (
    <td colSpan={colSpan || 1} className="border-0">
      <div
        className={`d-flex flex-column flex-md-row switch-toggle text-white rounded-pill border ${
          disableInputs ? "bg-secondary border-secondary" : "bg-dark border-dark"
        }`}
      >
        <ToggleState id={`${uid}-on`} labelText="Yes" checkedState={true} {..._props} />
        <ToggleState id={`${uid}-na`} labelText="N/A" checkedState={null} {..._props} />
        <ToggleState id={`${uid}-off`} labelText="No" checkedState={false} {..._props} />
      </div>
    </td>
  );
}

const rowComponentMap = {
  standard: ValueCommentRow,
  boolean: ValueCommentRow,
  searchable_children: SearchChildrenRow,
  select_children: SelectChildrenRow,
  selectable_twin_children: SelectTwinChildrenRow,
};

const getRowComponent = (score) => {
  let inputType = score.getIn(["meta_json", "input_type"]) || "standard";
  return rowComponentMap[inputType] || ValueCommentRow;
};

function MetricName(props) {
  let { tree, score, indentLevel, hasChildren, isCollapsed, onCollapseClick } = props;
  let name = score.getIn(["metric", "name"]);
  let treeId = tree.get("uid");

  let nameComponent = hasChildren ? (
    <a data-toggle="collapse" href={`#${treeId}`} aria-expanded={"false"} onClick={onCollapseClick}>
      <Octicon className="expand_caret" name={isCollapsed ? "triangle-right" : "triangle-down"} />
      {name}
    </a>
  ) : (
    <React.Fragment>
      {/* <Octicon name={"primitive-dot"} /> */}
      {name}
    </React.Fragment>
  );

  return <span style={{ margin: "0", marginLeft: `${indentLevel * 1.5}em` }}>{nameComponent}</span>;
}

function MetricTooltip({ score }) {
  let descr = score.getIn(["metric", "description"]);
  if (!descr) {
    return null;
  }
  return (
    <Tooltip
      animateFill={false}
      html={
        <div className="text-left">
          <div dangerouslySetInnerHTML={{ __html: descr.trim() }} />
        </div>
      }
    >
      <Octicon name="info" className="ml-2" />
    </Tooltip>
  );
}

function NumSubDocs({ subDocs }) {
  let docNumStr = subDocs ? (subDocs.length ? `(${subDocs.length} docs) | ` : "") : "";
  if (!docNumStr) {
    return null;
  }
  return <span>{docNumStr}</span>;
}

const showConversionFactors = (e, props) => {
  e.preventDefault();
  let text = "";
  let { conversionFactor } = props;
  if (!conversionFactor) {
    alert("No conversion factor found");
  }

  let fromMetric = conversionFactor.getIn(["metric", "name"]);
  let fromUnit = conversionFactor.getIn(["metric", "unit"]);
  let toUnit = conversionFactor.get("toUnit");
  let factor = conversionFactor.get("factor");

  text += `${fromMetric}:\n`;
  text += `${fromUnit} -> ${toUnit} = ${factor}\n\n`;
  alert(text);
};

const showChildConversions = (e, props) => {
  e.preventDefault();
  let { childConversions: conversions } = props;
  let text = "Conversions from sub-metrics:\n\n";
  for (let conversion of conversions) {
    text += `${conversion.getIn(["fromMetric", "name"])}:\n`;
    text += `${conversion.get("inputValue")} ${conversion.getIn(["fromMetric", "unit"])} -> ${conversion.get(
      "convertedValue"
    )} ${conversion.get("toUnit")}\n`;
    text += `(conversion factor: ${conversion.get("conversionFactor")})\n\n`;
  }
  alert(text);
};

const getDropDownLinks = (props) => {
  let { parentScore, hasChildren, calculated } = props;

  let thisItems = [];
  // Give the user the option to input manually
  if (calculated) {
    thisItems.push({
      name: "Show derivation from sub-metrics",
      onClick: (e) => showChildConversions(e, props),
    });
  }

  // Show conversion factors to parent
  if (parentScore) {
    thisItems.push({
      name: "Show conversion factors to parent",
      onClick: (e) => showConversionFactors(e, props),
    });
  }

  return thisItems;
};

function StateSaved({ isSaved }) {
  if (!isSaved) {
    return null;
  }
  return <Octicon name="check" />;
}

function MetricValueHistory(props) {
  let {
    context: { historicInputsByBaseMetric },
    score,
  } = props;

  const unit = score.metric.unit;
  const baseMetricId = score.metric.uid;
  const values = historicInputsByBaseMetric[baseMetricId] || [];

  console.log("Showing MetricValueHistory with", values);

  let data = {
    labels: values.map((i) => rpName(Im.Map(i))),
    datasets: [
      {
        data: values.map((i) => i.value),
        label: "",
        backgroundColor: "#b3d9ff",
      },
    ],
  };

  let chartData = {
    type: "bar",
    options: {
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        yAxes: [
          {
            scaleLabel: {
              display: true,
              labelString: unit,
            },
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    },
    data,
  };

  console.log("Rendering MetricValueHistory with data", chartData);

  return (
    <ErrorBoundary>
      <div>
        <ChartJSWrapper chartData={chartData} />
      </div>
    </ErrorBoundary>
  );
}

function PastInputModal(props) {
  let { score } = props;
  let MODAL_ID = "ReportMetricHistoryModal-001";
  return (
    <span
      className="mt-1 p-0 btn btn-sm"
      style={{ cursor: "pointer" }}
      onClick={() => {
        clearModal(MODAL_ID);
        setTimeout(() => {
          setAndShowModal(MODAL_ID, {
            title: `Previous data for: ${score?.getIn(["metric", "name"])}`,
            body: <MetricValueHistory unit={score?.getIn(["metric", "unit"])} {...props} />,
          });
        }, 250);
      }}
    >
      <Octicon name="graph" />
    </span>
  );
}

export function RowAdminPanel(props) {
  let { subDocs, isSaved, conversionFactor, disableInputs, disableComments } = props;

  if (disableComments) return null;

  if (disableInputs) {
    return (
      <div className="m-0 p-0 text-nowrap d-flex align-items-center justify-content-center">
        <PastInputModal {...props} />
      </div>
    );
  }
  let dropDownLinks = getDropDownLinks(props);
  return (
    <div className="m-0 p-0 text-nowrap d-flex align-items-center justify-content-center">
      <NumSubDocs subDocs={subDocs} />
      <StateSaved isSaved={isSaved} />
      <PastInputModal {...props} />
      {dropDownLinks?.length && isSaved ? "|" : null}
      <Hider hide={true}>
        <MetricAlerts {...conversionFactor} />
      </Hider>
    </div>
  );
}

export function NotRequiredMsg({ dueDate, freq_num, freq_unit }) {
  return (
    <span>
      Due every {freq_num} {freq_unit}. Next due: {dueDate.format("Do MMM YY")}
    </span>
  );
}

function NotRequiredCells(props) {
  let { disableInputs } = props;
  return (
    <td colSpan={disableInputs ? 3 : 4} style={{ verticalAlign: "middle" }} className="border-0">
      <NotRequiredMsg {...props} />
    </td>
  );
}

export const isNotInFrequency = ({ value, isRequiredPeriodically }) => {
  return !value && isRequiredPeriodically && !isRequiredPeriodically.dueThisReport;
};

function RowInputCells(props) {
  let {
    score,
    isRequiredPeriodically,
    ValueInputComponent,
    valueColSpan,
    commentColSpan,
    hideUnits,
    hideNoReport,
    disableInputs,
  } = props;

  ValueInputComponent = ValueInputComponent || ReportValueInputCell;

  if (isNotInFrequency(props)) {
    return <NotRequiredCells {...isRequiredPeriodically} disableInputs={disableInputs} />;
  }

  return (
    <CellSpanErrorBoundary colSpan={4}>
      <MetricUnitCell score={score} hide={hideUnits} />
      <ValueInputComponent {...props} colSpan={valueColSpan || 1} />
      <ReportCommentCell {...props} colSpan={commentColSpan || 1} />
      <ReportNoInputCheckbox {...props} hide={hideNoReport} />
    </CellSpanErrorBoundary>
  );
}

export function MetricNameDescriptionCell(props) {
  let { score } = props;
  return (
    <td className="border-0">
      <MetricName {...props} />
      <RequiredStar score={score} />
      <MetricTooltip {...props} />
    </td>
  );
}

function MetricUnitCell({ score, hide }) {
  if (hide) {
    return <td className="border-0" />;
  }
  return <td className="border-0">{score.getIn(["metric", "unit"])}</td>;
}

function ValueCommentRow(props) {
  let { score, rowStyle, calculated, noReport, exclSubNodes, value } = props;

  let highlight = (score.get("period") || -1) > 0;
  let _noReport = calculated ? false : noReport;

  let inputType = score.getIn(["meta_json", "input_type"]) || "standard";
  let isBool = inputType == "boolean";
  let ValueInputComponent = isBool ? BooleanToggleCell : ReportValueInputCell;

  if (exclSubNodes == undefined && score.getIn(["meta_json", "hideChildrenIfNullOrZero"])) {
    exclSubNodes = value?.get("value") ? exclSubNodes : true;
  }

  // As per ASS-1457 - hide input cells if requested in meta_json
  let hideInputCells = score?.getIn(["meta_json", "hideInputCells"]);
  if (hideInputCells) {
    return (
      <React.Fragment>
        <tr style={rowStyle?.toJS()} className={highlight ? "font-weight-bold" : ""}>
          <MetricNameDescriptionCell {...props} />
          <CellSpanErrorBoundary colSpan={5}>
            <td className="border-0"></td>
            <td className="border-0"></td>
            <td className="border-0"></td>
            <td className="border-0"></td>
            <td className="border-0"></td>
          </CellSpanErrorBoundary>
        </tr>
        <RowErrorBoundary>
          <SubNodes {...props} parentNoReport={_noReport} hide={exclSubNodes} />
        </RowErrorBoundary>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <tr style={rowStyle?.toJS()} className={highlight ? "font-weight-bold" : ""}>
        <MetricNameDescriptionCell {...props} />
        <RowInputCells
          {...props}
          ValueInputComponent={ValueInputComponent}
          valueColSpan={1}
          commentColSpan={1}
          hideUnits={isBool}
          hideNoReport={isBool}
        />
        <td className="border-0 p-0 m-0 d-flex justify-content-center">
          <MiniErrorBoundary>
            <RowAdminPanel {...props} />
          </MiniErrorBoundary>
        </td>
      </tr>
      <RowErrorBoundary>
        <SubNodes {...props} parentNoReport={_noReport} hide={exclSubNodes || isNotInFrequency(props)} />
      </RowErrorBoundary>
    </React.Fragment>
  );
}

function SearchChildrenRow(props) {
  return (
    <RowErrorBoundary>
      <RowErrorBoundary>
        <ValueCommentRow {...props} exclSubNodes={true} />
      </RowErrorBoundary>
      <RowErrorBoundary>
        <SearchChildRow {...props} hide={isNotInFrequency(props)} />
      </RowErrorBoundary>
    </RowErrorBoundary>
  );
}

function SelectChildrenRow(props) {
  let { rowStyle } = props;
  return (
    <RowErrorBoundary>
      <RowErrorBoundary>
        <tr style={rowStyle?.toJS()} className={""}>
          <MetricNameDescriptionCell {...props} />
          <td className="border-0"></td>
          <td className="border-0"></td>
          <td className="border-0"></td>
          <td className="border-0"></td>
          <td className="border-0"></td>
        </tr>
      </RowErrorBoundary>
      <RowErrorBoundary>
        <SelectChildRow {...props} hide={isNotInFrequency(props)} />
      </RowErrorBoundary>
    </RowErrorBoundary>
  );
}

function SelectTwinChildrenRow(props) {
  return (
    <RowErrorBoundary>
      <RowErrorBoundary>
        <ValueCommentRow {...props} rowStyle={Im.Map({ display: "none" })} exclSubNodes={true} />
      </RowErrorBoundary>
      <RowErrorBoundary>
        <SelectTwinChildRow {...props} />
      </RowErrorBoundary>
    </RowErrorBoundary>
  );
}

export default function ReportScoreRow(props) {
  let {
    indentLevel,
    disableInputs,
    path,
    currentReportingPeriod,
    InputComponent,
    parentPath,
    rowStyle,
    disableComments,
    context: { getNode, getParentPath, conversionFactorMap, inputValueMap, isCalculated },
  } = props;

  if (parentPath) {
    console.log("ReportScoreRow", path, parentPath);
  }

  // Get the node details, parent and related conversion factor
  let score = getNode(path);
  parentPath = parentPath || getParentPath(path);
  let parentNode = getNode(parentPath);
  let parentScore = parentNode?.get("_type") == "metricscore" ? parentNode : null;
  let conversionFactor = getConversionFactor({
    score,
    parentScore,
    convFactorDetails: { convFactorMap: conversionFactorMap },
  });

  // Is this input required in this reporting period
  let isRequiredPeriodically = isRequiredOnFreq(score, currentReportingPeriod);

  // Get the value that has been entered for this metric
  let baseMetricId = score.getIn(["metric", "uid"]);
  let value = inputValueMap.get(baseMetricId);
  let comment = value?.get("comment");

  /**********************
   ACTIONS
   **********************/

  const noReport = value?.getInPath("meta_json.noReport", false) || false;
  const disableValueInput = value?.getInPath("meta_json.disableValueInput", false) || false;

  let isHidden = score.getIn(["meta_json", "hidden"]);
  const calculated = isCalculated(score);
  rowStyle = rowStyle || Im.fromJS({});

  if (isHidden) {
    rowStyle = rowStyle.set("display", "none");
  } else {
    rowStyle = rowStyle.set("display", "");
  }

  // Get the component to render for this row - and extra props to pass to it (from state)
  let RowComponent = InputComponent || getRowComponent(score);
  let _props = {
    ...props,
    indentLevel: isHidden ? indentLevel - 1 : indentLevel,
    calculated,
    noReport,
    rowStyle,
    disableInputs,
    disableValueInput,
    disableComments,
    value,
    comment,
    score,
    conversionFactor,
    isRequiredPeriodically,
  };

  return (
    <RowErrorBoundary>
      <RowComponent {..._props} />
    </RowErrorBoundary>
  );
}

export const nestBaseMetric = (state, score) => {
  if (!state || !score) {
    return null;
  }
  // If the baseMetric is not already nested in the score then do it now
  let baseMetricId = score.getIn(["metric", "uid"]);
  if (!baseMetricId) {
    baseMetricId = score.get("metric");
    if (baseMetricId) {
      return score.set("metric", state.getIn([`entities`, `baseMetrics`, `byId`, `${baseMetricId}`]));
    }
  }
  return score;
};
